import React, { useState } from "react";
import axios from "axios";
import { MagnifyingGlass, Globe, Phone, ArrowRight, Spinner, LinkSimple } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function GradeBadge({ grade }) {
  return (
    <span className={`inline-block px-2.5 py-0.5 text-xs font-bold grade-${(grade || "f").toLowerCase()}`}>
      {grade || "?"}
    </span>
  );
}

export default function LeadScanner() {
  const [industry, setIndustry] = useState("");
  const [location, setLocation] = useState("");
  const [singleUrl, setSingleUrl] = useState("");
  const [results, setResults] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [analyzingUrl, setAnalyzingUrl] = useState(false);
  const navigate = useNavigate();

  const handleScan = async (e) => {
    e.preventDefault();
    if (!industry.trim() || !location.trim()) {
      toast.error("Enter both industry and location");
      return;
    }
    setScanning(true);
    setResults([]);
    try {
      const res = await axios.post(`${API}/leads/scan`, {
        industry: industry.trim(),
        location: location.trim(),
        limit: 10,
        include_gbp: true,
      });
      setResults(res.data.leads || []);
      if (res.data.leads?.length > 0) {
        toast.success(`Found ${res.data.leads.length} leads`);
      } else {
        toast.info("No leads found. Try different keywords or location.");
      }
    } catch (e) {
      toast.error("Scan failed. Try again.");
      console.error(e);
    } finally {
      setScanning(false);
    }
  };

  const handleAnalyzeUrl = async (e) => {
    e.preventDefault();
    if (!singleUrl.trim()) {
      toast.error("Enter a URL to analyze");
      return;
    }
    setAnalyzingUrl(true);
    try {
      const res = await axios.post(`${API}/leads/analyze-url?url=${encodeURIComponent(singleUrl.trim())}`);
      // Save as lead
      const leadRes = await axios.post(`${API}/leads`, {
        business_name: res.data.business_name || "",
        url: res.data.url,
        industry: industry || "General",
        location: location || "",
        phones: res.data.phones || [],
        emails: res.data.emails || [],
        quality_score: res.data.quality_score || 0,
        quality_grade: res.data.quality_grade || "?",
        quality_details: res.data.quality_details || {},
        description: res.data.description || "",
      });
      setResults(prev => [leadRes.data, ...prev]);
      toast.success("URL analyzed and saved as lead");
      setSingleUrl("");
    } catch (e) {
      toast.error("Could not analyze that URL");
      console.error(e);
    } finally {
      setAnalyzingUrl(false);
    }
  };

  return (
    <div data-testid="lead-scanner-page" className="space-y-6">
      <div>
        <h1
          className="text-3xl sm:text-4xl font-black tracking-tighter"
          style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
        >
          Lead Scanner
        </h1>
        <p className="text-sm text-[#52525B] mt-1">
          Find businesses with terrible websites. Extract phone numbers. Build them something better.
        </p>
      </div>

      {/* Scan Forms */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Bulk Scan */}
        <div className="bg-white border border-border p-5">
          <h3 className="text-base font-bold tracking-tight mb-3" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            <MagnifyingGlass weight="bold" size={16} className="inline mr-2 text-[#002FA7]" />
            Bulk Scan by Industry + Location
          </h3>
          <form onSubmit={handleScan} className="space-y-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] block mb-1">Industry / Niche</label>
              <Input
                data-testid="scan-industry-input"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="e.g. Plumber, Dentist, HVAC, Lawyer"
                className="border-border rounded-none focus:border-[#002FA7] focus:ring-0"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] block mb-1">Location</label>
              <Input
                data-testid="scan-location-input"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g. Miami FL, Austin TX, Los Angeles"
                className="border-border rounded-none focus:border-[#002FA7] focus:ring-0"
              />
            </div>
            <button
              data-testid="scan-submit-btn"
              type="submit"
              disabled={scanning}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002482] transition-colors duration-100 disabled:opacity-50"
            >
              {scanning ? (
                <>
                  <div className="spinner" style={{ borderTopColor: "white", borderColor: "rgba(255,255,255,0.3)" }} />
                  Scanning...
                </>
              ) : (
                <>
                  <MagnifyingGlass weight="bold" size={16} />
                  Scan for Leads
                </>
              )}
            </button>
          </form>
        </div>

        {/* Single URL */}
        <div className="bg-white border border-border p-5">
          <h3 className="text-base font-bold tracking-tight mb-3" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            <LinkSimple weight="bold" size={16} className="inline mr-2 text-[#002FA7]" />
            Analyze Single Website
          </h3>
          <form onSubmit={handleAnalyzeUrl} className="space-y-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] block mb-1">Website URL</label>
              <Input
                data-testid="analyze-url-input"
                value={singleUrl}
                onChange={(e) => setSingleUrl(e.target.value)}
                placeholder="e.g. joesplumbing.com"
                className="border-border rounded-none focus:border-[#002FA7] focus:ring-0"
              />
            </div>
            <p className="text-xs text-[#52525B]">
              Scores the website on mobile, SEO, design, and more. Extracts phone + email.
            </p>
            <button
              data-testid="analyze-url-btn"
              type="submit"
              disabled={analyzingUrl}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-[#0A0A0A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#0A0A0A] hover:text-white transition-colors duration-100 disabled:opacity-50"
            >
              {analyzingUrl ? (
                <>
                  <div className="spinner" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Globe weight="bold" size={16} />
                  Analyze Website
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div className="bg-white border border-border">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h3 className="text-base font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              Scan Results ({results.length})
            </h3>
            <span className="text-xs text-[#52525B]">Sorted by worst quality first</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full dense-table">
              <thead>
                <tr>
                  <th className="text-left">Business</th>
                  <th className="text-left">Website</th>
                  <th className="text-left">Phone</th>
                  <th className="text-left">Email</th>
                  <th className="text-center">Score</th>
                  <th className="text-center">Grade</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {results.map((lead, i) => (
                  <tr key={lead.id || i} className="animate-fade-in-up" style={{ animationDelay: `${i * 0.05}s` }}>
                    <td className="font-medium max-w-[200px] truncate">
                      {lead.business_name || "—"}
                    </td>
                    <td className="max-w-[180px] truncate">
                      {lead.url ? (
                        <a href={lead.url} target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline text-xs font-mono">
                          {lead.domain || lead.url}
                        </a>
                      ) : "—"}
                    </td>
                    <td className="font-mono text-xs">
                      {lead.phones?.length > 0 ? (
                        <span className="flex items-center gap-1">
                          <Phone weight="bold" size={12} className="text-[#00C853]" />
                          {lead.phones[0]}
                        </span>
                      ) : (
                        <span className="text-[#A1A1AA]">none</span>
                      )}
                    </td>
                    <td className="text-xs max-w-[160px] truncate">
                      {lead.emails?.[0] || <span className="text-[#A1A1AA]">none</span>}
                    </td>
                    <td className="text-center font-mono text-sm font-bold">
                      {lead.quality_score || 0}/100
                    </td>
                    <td className="text-center">
                      <GradeBadge grade={lead.quality_grade} />
                    </td>
                    <td className="text-right">
                      <button
                        data-testid={`build-site-${i}`}
                        onClick={() => navigate(`/generator?leadId=${lead.id}&name=${encodeURIComponent(lead.business_name || "")}&industry=${encodeURIComponent(industry || lead.industry || "")}`)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#002FA7] text-white hover:bg-[#002482] transition-colors"
                      >
                        Build Site
                        <ArrowRight size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
